import { useCallback, useEffect, useMemo, useState } from "react";
import withRouter from "Components/Common/withRouter";
import OrganizationChartTemplate from "Components/templates/hr/organization-chart"
import { createSelector } from "reselect";
import { HumanResourcesSliceState } from "slices/human-resources/reducer";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { biuldOrgChartTree, getCharts, getOrgChart, reduceChildMap, reduceNodes } from "slices/human-resources/thunk";
import { D3OrgChartNode } from "models/org-chart-node";

const selectHumanResources = createSelector(
  (state: any) => state.HumanResources,
  ({ orgChartNodes, charts }: HumanResourcesSliceState) => ({
    orgChartNodes, charts
  })
)

// Department has no stable id across environments (reseeded per-env via UUID()), so these
// deep-link routes identify their target department by its stable `code` instead.
interface FullScreenOrgChartProps {
  departmentCode: string
}

const FullScreenOrgChart = ({ departmentCode }: FullScreenOrgChartProps) => {
  const dispatch: any = useDispatch();
  const { orgChartNodes, charts } = useSelector(selectHumanResources);
  const [tree, setTree] = useState<Map<string, D3OrgChartNode[]>>(new Map());

  const chart = useMemo(() => charts.find(c => c.code === departmentCode) ?? null, [charts, departmentCode])

  document.title = `${chart?.description ?? "Organization Chart"} | IBexIQ`;

  const data = useMemo(() => reduceNodes(tree, orgChartNodes, chart), [tree, orgChartNodes, chart, reduceNodes])
  const children = useMemo(() => reduceChildMap(tree), [tree, reduceChildMap])


  useEffect(() => {
    if (orgChartNodes.length === 0) {
      dispatch(getOrgChart())
    }

    if (charts.length === 0) {
      dispatch(getCharts())
    }
  }, [])

  // Organize chart into tree
  useEffect(() => {
    setTree(biuldOrgChartTree(orgChartNodes))
  }, [orgChartNodes, setTree, biuldOrgChartTree])

  const getViewPortDemensions = () => ({
    width: window.innerWidth,
    height: window.innerHeight
  })

  useEffect(() => {
    const topbar = document.getElementById('page-topbar');
    const content = document.getElementsByClassName('page-content');
    const sidebar = document.getElementsByClassName('vertical-menu')
    const mainContent = document.getElementsByClassName("main-content");
    const orgChart = document.getElementsByClassName("svg-chart-container")

    const logo = document.getElementsByClassName("logo-container")
    const actions = document.getElementsByClassName("action-button-container")
    const arrow = document.getElementsByClassName("arrow-button-container")
    const select = document.getElementsByClassName("select-chart-container")


    if (topbar)
      topbar.style.display = 'none'


    Array.from(sidebar).forEach(e => {
      if (e instanceof HTMLElement) {
        e.style.display = 'none'
      }
    })

    Array.from(content).forEach(d => {
      if (d instanceof HTMLElement) {
        d.style.padding = '0px'
      }
    })

    Array.from(mainContent).forEach(e => {
      if (e instanceof HTMLElement) {
        e.style.margin = '0px'
        e.style.padding = '0px'
        e.style.boxSizing = 'border-box'
        e.style.minHeight = '100vh'
        e.style.minWidth = '100vw'
      }
    })

    Array.from(orgChart).forEach(e => {
      if (e instanceof SVGElement) {
        const { width, height } = getViewPortDemensions()
        e.setAttribute('width', width.toString())
        e.setAttribute('height', height.toString())
      }
    })

    Array.from(actions).forEach(e => {
      if (e instanceof HTMLElement) {
        e.style.top = '10px'

      }
    })


    Array.from(arrow).forEach(e => {
      if (e instanceof HTMLElement) {
        e.style.top = '10px'
      }
    })

    Array.from(select).forEach(e => {
      if (e instanceof HTMLElement) {
        e.style.top = '10px'
      }
    })



    if (orgChartNodes.length === 0) {
      dispatch(getOrgChart())
    }
  }, [])


  return <OrganizationChartTemplate
    fullScreen
    childrenMap={children}
    pageStyles={{
      padding: 0,
      minWidth: '100vh',
      boxSizing: 'border-box',
      minHeight: '100vh'
    }}
    data={data.map(n => JSON.parse(JSON.stringify(n)))}
    focusedChart={chart}
    isOpen={false}
    onModalClose={() => { }}
  />

}

export default FullScreenOrgChart
